/**
 * Quiz Screen
 * Shows available quizzes for weekend mode or category selection for free play
 */

import { useState, useEffect, useCallback } from "react";
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  RefreshControl,
  ActivityIndicator,
  Alert,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { Ionicons } from "@expo/vector-icons";
import { useRouter, useLocalSearchParams } from "expo-router";
import { useTheme, useThemeColors } from "@/providers/ThemeProvider";
import { useAuth } from "@/providers/AuthProvider";
import { supabase } from "@/lib/supabase/client";
import { format } from "date-fns";
import { nl } from "date-fns/locale";

/**
 * Quiz type from database
 */
interface Quiz {
  id: string;
  name: string;
  description: string | null;
  icon: string;
  color: string;
  question_count: number;
  time_per_question: number;
  points_per_correct: number;
  bonus_speed: number;
  is_active: boolean;
  is_weekend_enabled: boolean;
  use_custom_dates: boolean;
  starts_at: string | null;
  ends_at: string | null;
  // Play restrictions
  is_team_based: boolean;
  allow_unlimited_plays: boolean;
  max_plays_per_user: number;
  max_plays_per_team: number;
  allow_retry_on_zero: boolean;
}

/**
 * Quiz with play status
 */
interface QuizWithStatus extends Quiz {
  playStatus: {
    canPlay: boolean;
    reason?: string;
    userPlays: number;
    teamPlays: number;
    playedBy: {
      userId: string;
      displayName: string;
      score: number;
    }[];
  };
}

/**
 * Quiz category from database
 */
interface QuizCategory {
  id: string;
  name: string;
  description: string | null;
  icon: string;
  color: string;
}

export default function QuizScreen() {
  const router = useRouter();
  const params = useLocalSearchParams<{ mode?: string }>();
  const { isDark } = useTheme();
  const colors = useThemeColors();
  const { activeProfile, activeWeekend } = useAuth();

  const mode = params.mode === "weekend" ? "weekend" : "free";

  // State
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [quizzes, setQuizzes] = useState<QuizWithStatus[]>([]);
  const [categories, setCategories] = useState<QuizCategory[]>([]);
  const [userTeamId, setUserTeamId] = useState<string | null>(null);

  /**
   * Fetch quizzes and categories
   */
  const fetchData = useCallback(async () => {
    if (!activeProfile) return;

    try {
      // Get user's team for weekend
      let teamId: string | null = null;
      if (activeWeekend) {
        const { data: teamMember } = await supabase
          .from("team_members")
          .select("team_id, teams!inner(weekend_id)")
          .eq("user_id", activeProfile.id)
          .eq("teams.weekend_id", activeWeekend.id)
          .single();
        
        teamId = teamMember?.team_id || null;
        setUserTeamId(teamId);
      }

      // Fetch weekend-enabled quizzes
      const { data: quizzesData, error: quizzesError } = await supabase
        .from("quizzes")
        .select("*")
        .eq("is_active", true)
        .eq("is_weekend_enabled", true)
        .order("created_at", { ascending: false });

      if (quizzesError) throw quizzesError;

      // For each quiz, check play status
      const quizzesWithStatus: QuizWithStatus[] = await Promise.all(
        (quizzesData || []).map(async (quiz) => {
          // Fetch completed sessions for this quiz
          const { data: sessions } = await supabase
            .from("game_sessions")
            .select(`
              id,
              created_by,
              participants,
              result,
              points_awarded
            `)
            .eq("quiz_id", quiz.id)
            .eq("status", "completed");

          const completedSessions = sessions || [];

          // Calculate user plays
          const userPlays = completedSessions.filter(
            (s) => s.created_by === activeProfile.id
          ).length;

          // Calculate team plays (if team-based)
          const teamPlays = teamId
            ? completedSessions.filter((s) =>
                s.participants?.some((p: any) => p.team_id === teamId)
              ).length
            : 0;

          // Get who played from the team
          const playedBy = teamId
            ? completedSessions
                .filter((s) =>
                  s.participants?.some((p: any) => p.team_id === teamId)
                )
                .map((s) => {
                  const participant = s.participants?.[0];
                  return {
                    userId: s.created_by || "",
                    displayName: participant?.display_name || "Onbekend",
                    score: s.points_awarded || 0,
                  };
                })
            : [];

          // Determine if user can play
          let canPlay = true;
          let reason: string | undefined;

          if (quiz.is_team_based) {
            // Team-based quiz: check team plays
            if (!quiz.allow_unlimited_plays && teamPlays >= quiz.max_plays_per_team) {
              // Check if retry on zero is allowed
              if (quiz.allow_retry_on_zero) {
                const teamScoreTotal = playedBy.reduce((sum, p) => sum + p.score, 0);
                if (teamScoreTotal === 0 && teamPlays < quiz.max_plays_per_team + 1) {
                  canPlay = true;
                } else {
                  canPlay = false;
                  reason = "team_played";
                }
              } else {
                canPlay = false;
                reason = "team_played";
              }
            }
          } else {
            // Individual quiz: check user plays
            if (!quiz.allow_unlimited_plays && userPlays >= quiz.max_plays_per_user) {
              canPlay = false;
              reason = "max_plays_reached";
            }
          }

          return {
            ...quiz,
            playStatus: {
              canPlay,
              reason,
              userPlays,
              teamPlays,
              playedBy,
            },
          };
        })
      );

      setQuizzes(quizzesWithStatus);

      // Fetch categories for free play
      const { data: categoriesData, error: categoriesError } = await supabase
        .from("quiz_categories")
        .select("*")
        .eq("is_active", true)
        .order("sort_order", { ascending: true });

      if (categoriesError) throw categoriesError;
      setCategories(categoriesData || []);
    } catch (error) {
      console.error("Error fetching quiz data:", error);
    } finally {
      setIsLoading(false);
    }
  }, [activeProfile, activeWeekend]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  /**
   * Handle refresh
   */
  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    await fetchData();
    setIsRefreshing(false);
  }, [fetchData]);

  /**
   * Check if quiz is available (within custom dates if set)
   */
  const isQuizAvailable = (quiz: Quiz): boolean => {
    if (!quiz.use_custom_dates) return true;
    
    const now = new Date();
    if (quiz.starts_at && new Date(quiz.starts_at) > now) return false;
    if (quiz.ends_at && new Date(quiz.ends_at) < now) return false;
    
    return true;
  };

  /**
   * Get quiz availability status text
   */
  const getQuizStatus = (quiz: Quiz): { text: string; color: string } | null => {
    if (!quiz.use_custom_dates) return null;

    const now = new Date();
    if (quiz.starts_at && new Date(quiz.starts_at) > now) {
      return {
        text: `Start ${format(new Date(quiz.starts_at), "d MMM HH:mm", { locale: nl })}`,
        color: "#F59E0B",
      };
    }
    if (quiz.ends_at) {
      return {
        text: `Tot ${format(new Date(quiz.ends_at), "d MMM HH:mm", { locale: nl })}`,
        color: "#10B981",
      };
    }
    return null;
  };

  /**
   * Handle quiz selection (weekend mode)
   */
  const handleSelectQuiz = (quiz: QuizWithStatus) => {
    if (!isQuizAvailable(quiz)) {
      Alert.alert(
        "Niet beschikbaar",
        "Deze quiz is nog niet gestart of al afgelopen."
      );
      return;
    }

    // Check if user can play
    if (!quiz.playStatus.canPlay) {
      if (quiz.playStatus.reason === "team_played") {
        const players = quiz.playStatus.playedBy
          .map((p) => `${p.displayName} (${p.score} pts)`)
          .join(", ");
        Alert.alert(
          "Team heeft al gespeeld",
          `Een teamlid heeft deze quiz al gespeeld: ${players}. ${
            quiz.allow_retry_on_zero && quiz.playStatus.playedBy.every((p) => p.score === 0)
              ? "Omdat de score 0 was, mag een ander teamlid het proberen."
              : "Elk team mag maar één keer spelen."
          }`
        );
        return;
      } else if (quiz.playStatus.reason === "max_plays_reached") {
        Alert.alert(
          "Maximum bereikt",
          `Je hebt deze quiz al ${quiz.playStatus.userPlays}x gespeeld. Maximum is ${quiz.max_plays_per_user}.`
        );
        return;
      }
    }

    // Show warning for team-based quiz
    if (quiz.is_team_based) {
      Alert.alert(
        "Team Quiz",
        "Let op: Als jij deze quiz speelt, kunnen je teamgenoten dat niet meer doen. De punten die je verdient gelden voor het hele team.\n\nWil je doorgaan?",
        [
          { text: "Annuleren", style: "cancel" },
          {
            text: "Start Quiz",
            onPress: () => {
              router.push(`/(tabs)/games/quiz/play?quizId=${quiz.id}&mode=weekend`);
            },
          },
        ]
      );
    } else {
      // Non-team quiz, start directly
      router.push(`/(tabs)/games/quiz/play?quizId=${quiz.id}&mode=weekend`);
    }
  };

  /**
   * Handle category selection (free play mode)
   */
  const handleSelectCategory = (categoryId: string | null) => {
    router.push(`/(tabs)/games/quiz/setup?categoryId=${categoryId || "all"}&mode=free`);
  };

  /**
   * Render weekend mode (show available quizzes)
   */
  const renderWeekendMode = () => (
    <>
      {/* Available Quizzes */}
      <View className="px-4 mt-4">
        <Text className="text-lg font-semibold mb-3" style={{ color: colors.text }}>
          Beschikbare Quizzen
        </Text>

        {quizzes.length === 0 ? (
          <View
            className="p-6 rounded-xl items-center"
            style={{ backgroundColor: colors.surface, borderWidth: 1, borderColor: colors.border }}
          >
            <Ionicons name="help-circle-outline" size={48} color={colors.textTertiary} />
            <Text className="mt-3 text-center" style={{ color: colors.textSecondary }}>
              Er zijn nog geen quizzen beschikbaar voor dit weekend
            </Text>
          </View>
        ) : (
          <View>
            {quizzes.map((quiz) => {
              const available = isQuizAvailable(quiz);
              const status = getQuizStatus(quiz);
              const maxPoints = quiz.question_count * (quiz.points_per_correct + quiz.bonus_speed);
              const canPlay = quiz.playStatus.canPlay && available;
              const hasPlayed = quiz.playStatus.userPlays > 0 || quiz.playStatus.teamPlays > 0;

              return (
                <TouchableOpacity
                  key={quiz.id}
                  onPress={() => handleSelectQuiz(quiz)}
                  disabled={!canPlay}
                  className="rounded-xl p-4 mb-3"
                  style={{
                    backgroundColor: canPlay ? `${quiz.color}10` : colors.surface,
                    borderWidth: 2,
                    borderColor: canPlay ? quiz.color : hasPlayed ? "#10B981" : colors.border,
                    opacity: canPlay ? 1 : 0.7,
                  }}
                  activeOpacity={0.7}
                >
                  {/* Badges row */}
                  {(quiz.is_team_based || hasPlayed) && (
                    <View className="flex-row flex-wrap mb-2 gap-2">
                      {quiz.is_team_based && (
                        <View
                          className="flex-row items-center px-2 py-1 rounded-full"
                          style={{ backgroundColor: "#F59E0B20" }}
                        >
                          <Ionicons name="people" size={12} color="#F59E0B" />
                          <Text className="ml-1 text-xs font-medium" style={{ color: "#F59E0B" }}>
                            Team Quiz
                          </Text>
                        </View>
                      )}
                      {hasPlayed && !quiz.playStatus.canPlay && (
                        <View
                          className="flex-row items-center px-2 py-1 rounded-full"
                          style={{ backgroundColor: "#10B98120" }}
                        >
                          <Ionicons name="checkmark-circle" size={12} color="#10B981" />
                          <Text className="ml-1 text-xs font-medium" style={{ color: "#10B981" }}>
                            Gespeeld
                          </Text>
                        </View>
                      )}
                      {!quiz.allow_unlimited_plays && quiz.playStatus.canPlay && (
                        <View
                          className="flex-row items-center px-2 py-1 rounded-full"
                          style={{ backgroundColor: colors.surfaceSecondary }}
                        >
                          <Ionicons name="refresh" size={12} color={colors.textSecondary} />
                          <Text className="ml-1 text-xs" style={{ color: colors.textSecondary }}>
                            {quiz.is_team_based
                              ? `${quiz.playStatus.teamPlays}/${quiz.max_plays_per_team}`
                              : `${quiz.playStatus.userPlays}/${quiz.max_plays_per_user}`}
                          </Text>
                        </View>
                      )}
                    </View>
                  )}

                  <View className="flex-row items-center">
                    {/* Icon */}
                    <View
                      className="w-14 h-14 rounded-xl items-center justify-center mr-4"
                      style={{ backgroundColor: canPlay ? `${quiz.color}25` : colors.surfaceSecondary }}
                    >
                      <Ionicons
                        name={quiz.icon as any}
                        size={28}
                        color={canPlay ? quiz.color : colors.textTertiary}
                      />
                    </View>

                    {/* Info */}
                    <View className="flex-1">
                      <Text className="text-lg font-bold" style={{ color: colors.text }}>
                        {quiz.name}
                      </Text>
                      {quiz.description && (
                        <Text
                          className="text-sm"
                          style={{ color: colors.textSecondary }}
                          numberOfLines={1}
                        >
                          {quiz.description}
                        </Text>
                      )}
                      <View className="flex-row items-center flex-wrap mt-1 gap-x-3">
                        <View className="flex-row items-center">
                          <Ionicons name="help-circle" size={14} color={colors.textTertiary} />
                          <Text className="ml-1 text-xs" style={{ color: colors.textTertiary }}>
                            {quiz.question_count} vragen
                          </Text>
                        </View>
                        <View className="flex-row items-center">
                          <Ionicons name="time" size={14} color={colors.textTertiary} />
                          <Text className="ml-1 text-xs" style={{ color: colors.textTertiary }}>
                            {quiz.time_per_question}s
                          </Text>
                        </View>
                        <View className="flex-row items-center">
                          <Ionicons name="star" size={14} color="#F59E0B" />
                          <Text className="ml-1 text-xs font-medium" style={{ color: "#F59E0B" }}>
                            max {maxPoints} pts
                          </Text>
                        </View>
                      </View>
                    </View>

                    {/* Arrow or Lock/Check */}
                    {canPlay ? (
                      <Ionicons name="chevron-forward" size={24} color={quiz.color} />
                    ) : hasPlayed ? (
                      <Ionicons name="checkmark-circle" size={24} color="#10B981" />
                    ) : (
                      <Ionicons name="lock-closed" size={20} color={colors.textTertiary} />
                    )}
                  </View>

                  {/* Show who played from team */}
                  {quiz.is_team_based && quiz.playStatus.playedBy.length > 0 && (
                    <View className="mt-3 pt-3" style={{ borderTopWidth: 1, borderTopColor: colors.border }}>
                      <Text className="text-xs font-medium mb-1" style={{ color: colors.textSecondary }}>
                        Gespeeld door:
                      </Text>
                      {quiz.playStatus.playedBy.map((player, idx) => (
                        <View key={idx} className="flex-row items-center justify-between">
                          <View className="flex-row items-center">
                            <Ionicons name="person" size={12} color={colors.textTertiary} />
                            <Text className="ml-1 text-sm" style={{ color: colors.text }}>
                              {player.displayName}
                            </Text>
                          </View>
                          <View className="flex-row items-center">
                            <Ionicons name="star" size={12} color="#F59E0B" />
                            <Text className="ml-1 text-sm font-bold" style={{ color: "#F59E0B" }}>
                              {player.score} pts
                            </Text>
                          </View>
                        </View>
                      ))}
                    </View>
                  )}

                  {/* Status Badge */}
                  {status && (
                    <View
                      className="mt-2 self-start px-2 py-1 rounded-full"
                      style={{ backgroundColor: `${status.color}20` }}
                    >
                      <Text className="text-xs font-medium" style={{ color: status.color }}>
                        {status.text}
                      </Text>
                    </View>
                  )}
                </TouchableOpacity>
              );
            })}
          </View>
        )}
      </View>

      {/* Points Info */}
      <View
        className="mx-4 mt-4 p-4 rounded-xl"
        style={{ backgroundColor: "#8B5CF610" }}
      >
        <View className="flex-row items-center mb-2">
          <Ionicons name="trophy" size={18} color="#8B5CF6" />
          <Text className="ml-2 font-semibold" style={{ color: "#8B5CF6" }}>
            Competitie Punten
          </Text>
        </View>
        <Text className="text-sm" style={{ color: colors.textSecondary }}>
          Speel quizzen om punten te verdienen voor je team!
          Snelle en correcte antwoorden leveren bonuspunten op.
        </Text>
      </View>
    </>
  );

  /**
   * Render free play mode (show categories)
   */
  const renderFreePlayMode = () => (
    <>
      {/* Category Selection */}
      <View className="px-4 mt-4">
        <Text className="text-lg font-semibold mb-3" style={{ color: colors.text }}>
          Kies een Categorie
        </Text>

        <View className="flex-row flex-wrap">
          {/* All Categories Option */}
          <TouchableOpacity
            onPress={() => handleSelectCategory(null)}
            className="w-[48%] rounded-xl p-4 mb-3 mr-[4%]"
            style={{
              backgroundColor: colors.surface,
              borderWidth: 2,
              borderColor: colors.border,
            }}
            activeOpacity={0.7}
          >
            <View
              className="w-10 h-10 rounded-lg items-center justify-center mb-2"
              style={{ backgroundColor: "#6366F130" }}
            >
              <Ionicons name="apps" size={20} color="#6366F1" />
            </View>
            <Text className="font-semibold" style={{ color: colors.text }}>
              Alle
            </Text>
            <Text className="text-xs" style={{ color: colors.textSecondary }}>
              Mix van alles
            </Text>
          </TouchableOpacity>

          {/* Category Options */}
          {categories.map((category, index) => (
            <TouchableOpacity
              key={category.id}
              onPress={() => handleSelectCategory(category.id)}
              className="w-[48%] rounded-xl p-4 mb-3"
              style={{
                backgroundColor: colors.surface,
                borderWidth: 2,
                borderColor: colors.border,
                marginRight: index % 2 === 0 ? "4%" : 0,
              }}
              activeOpacity={0.7}
            >
              <View
                className="w-10 h-10 rounded-lg items-center justify-center mb-2"
                style={{ backgroundColor: `${category.color}30` }}
              >
                <Ionicons
                  name={category.icon as any}
                  size={20}
                  color={category.color}
                />
              </View>
              <Text
                className="font-semibold"
                style={{ color: colors.text }}
                numberOfLines={1}
              >
                {category.name}
              </Text>
              {category.description && (
                <Text
                  className="text-xs"
                  style={{ color: colors.textSecondary }}
                  numberOfLines={1}
                >
                  {category.description}
                </Text>
              )}
            </TouchableOpacity>
          ))}
        </View>
      </View>

      {/* Info */}
      <View
        className="mx-4 mt-4 p-4 rounded-xl"
        style={{ backgroundColor: colors.surface, borderWidth: 1, borderColor: colors.border }}
      >
        <View className="flex-row items-center mb-2">
          <Ionicons name="game-controller" size={18} color={colors.primary} />
          <Text className="ml-2 font-semibold" style={{ color: colors.text }}>
            Vrij Spelen
          </Text>
        </View>
        <Text className="text-sm" style={{ color: colors.textSecondary }}>
          Oefen je kennis zonder punten te verdienen.
          Kies een categorie om te beginnen!
        </Text>
      </View>
    </>
  );

  return (
    <SafeAreaView className="flex-1" style={{ backgroundColor: colors.background }} edges={["top", "left", "right"]}>
      <ScrollView
        className="flex-1"
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl
            refreshing={isRefreshing}
            onRefresh={handleRefresh}
            tintColor={colors.primary}
          />
        }
      >
        {/* Header */}
        <View className="px-4 py-4 flex-row items-center">
          <TouchableOpacity
            onPress={() => router.back()}
            className="w-10 h-10 rounded-full items-center justify-center mr-3"
            style={{ backgroundColor: colors.surface }}
            accessibilityRole="button"
            accessibilityLabel="Terug"
          >
            <Ionicons name="arrow-back" size={22} color={colors.text} />
          </TouchableOpacity>
          <View className="flex-1">
            <Text className="text-2xl font-bold" style={{ color: colors.text }}>
              Quiz
            </Text>
            <Text style={{ color: colors.textSecondary }}>
              {mode === "weekend" ? "Familie Weekend" : "Vrij Spelen"}
            </Text>
          </View>
          {mode === "weekend" && (
            <View
              className="px-3 py-1 rounded-full"
              style={{ backgroundColor: "#8B5CF620" }}
            >
              <Text className="text-xs font-medium" style={{ color: "#8B5CF6" }}>
                🏆 Punten
              </Text>
            </View>
          )}
        </View>

        {/* Loading */}
        {isLoading ? (
          <View className="py-12 items-center">
            <ActivityIndicator color={colors.primary} size="large" />
            <Text className="mt-3" style={{ color: colors.textSecondary }}>
              Quizzen laden...
            </Text>
          </View>
        ) : (
          <>
            {mode === "weekend" ? renderWeekendMode() : renderFreePlayMode()}
          </>
        )}

        <View className="h-8" />
      </ScrollView>
    </SafeAreaView>
  );
}
